import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useRecording } from "../../context/RecordingContext";
import blinkscribeapi from "../../api/blinkscribeapi";
import { eventBus } from "../../utils/eventBus";
import { careiqOrdersShown, getPracticeId } from "../../config/practice";
import { classifyVisit, visitNotHeld, visitTimeLabel } from "../../utils/visitGuard";
import { normalizeApptId } from "../../utils/recordingCompletion";
import { v2StoreKey } from "../../landingscreen/patientcharts/tabs/careiq/N3/orderV2";
import { placedWhenEditable, visitOrderContext } from "../../landingscreen/patientcharts/tabs/careiq/N3/orderCreate";
import { emptyRecord, readRecord, reviewStoreKey, visitKeyOf, writeRecord } from "./orderReviewModel";
import {
  LIVE_NEW_MS, LIVE_POLL_MS, LIVE_UNDO_MS, cardRows, clearInflight, inflightIn, liveStateFor, markShown, newerLive, openCount,
  orderStillOnVisit, recordDismissed, recordInflight, recordOrdered, recordUndone, rowAge,
} from "./liveOrdersModel";
import {
  beginFlight, cardMounted, changeLiveOrderWhen, endFlight, hideSurface, isFailed, isInflight, markSurface, placeLiveRow,
  claimPoll, registerLiveVisit, releasePoll, sharedVisitOrders, surfaceIsHidden, undoLiveOrder, visitOrdersOf,
} from "./liveOrdersVisit";
import { setOrderReviewLabel } from "./OrderReviewHost";
import { otherWhen, recordWhen, whenFor } from "./orderWhen";
import WhenToggle from "./WhenToggle";

/* The live one-tap Orders card (owner decision 2026-10-23; model in liveOrdersModel.js, BlinkScribe live.py).

   In the chart's LiveScribe panel while the visit is recorded (and after the stop while rows are left), for the
   providers BlinkScribe enables (payload / socket live_orders.enabled; everyone else: nothing renders and the old
   cards stay). Rows arrive over the recording socket as they are heard: what the provider SAID ("You said: ..."),
   what the conversation agreed on ("Discussed"), what the schedule says ("Scheduled ... today"). Each row has ONE
   button, Order, which places that order at once through the same orders/create call and body as CareIQ's Add
   (liveOrdersVisit.js -> N3/orderCreate.js); then "Ordered ✓" with Undo for 2 minutes (the same patient-scoped
   delete as CareIQ's Undo). Nothing is ever placed without the tap. A row whose order is already on the visit
   (placed by hand) disappears by itself (the visit's orders are re-read every 20 s while rows are open, and on every
   order change). × dismisses a row (it is not offered again, not even in the end-of-visit review).
   Monitoring (BlinkScribe feedback, concept ids only): live_shown / live_ordered / live_undone / live_dismissed /
   live_failed with the row's source and how long it had been on screen.
   Review 10-23: a tap re-reads the visit's record first (another card / tab may have placed it) and holds a
   page-wide in-flight slot per visit + concept (liveOrdersVisit.beginFlight), so one row is never placed twice;
   every record change emits "orderReviewChanged" (and other browser tabs see the localStorage "storage" event).
   Today / Schedule (owner 10-24, orderWhen.js): every open row has a Today (green) / Schedule (orange) toggle with
   BlinkScribe's default; Order places with that mode (orders/create schedule "f" / "t"); an ordered row keeps the
   toggle: a tap changes the placed order through the dashboard's existing tasks/update_status_schedule call, only
   after checking the order is on this visit, and shows the result or the error.
   Surfaces (hubfix-oi-1026, owner 10-26; live 10-26: 79 rows shown, 0 taps): the same card, model, placement path
   and guards also render where orders are entered - the Orders popup / page (surface "orders") and the dashboard's
   expanded card (surface "dashboard"), through LiveOrdersSlot (9928 only). There it is compact (at most
   COMPACT_ROWS rows, then "Show all") and its ✕ hides it on that surface until a new row arrives. Only the chart's
   card counts as "this visit's chart is open" for the end-of-visit review (cardMounted). Every live_* line carries
   can_order, disabled_reason (orderDisabledReason) and the surface; live_surface is sent once per visit + surface
   with the row count. */

const box = { margin: "6px 0 8px", padding: "8px 10px", border: "1px solid #BFDBFE", borderRadius: 8, background: "#F5F9FF", textAlign: "left" };
const titleS = { display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: "#1E3A8A", marginBottom: 4 };
const rowS = { display: "flex", alignItems: "center", gap: 8, padding: "4px 4px", fontSize: 12, color: "#1F2937", borderRadius: 6 };
const whyS = { fontSize: 10, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const btnS = {
  marginLeft: "auto", border: "1px solid #2563EB", background: "#2563EB", color: "#fff", borderRadius: 6, padding: "3px 10px",
  fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
};
const linkS = { border: "none", background: "none", color: "#2563EB", cursor: "pointer", fontSize: 11, padding: "0 2px", textDecoration: "underline" };
const xS = { border: "none", background: "none", color: "#9CA3AF", cursor: "pointer", fontSize: 12, lineHeight: 1, padding: "0 2px" };

const storedLive = (patientId, apptKey) => {
  try {
    const raw = localStorage.getItem(v2StoreKey(patientId, apptKey));
    const st = raw ? JSON.parse(raw) : null;
    return st && st.payload && st.payload.live_orders ? st.payload.live_orders : null;
  } catch (_) {
    return null;
  }
};

const feedback = (patientId, apptKey, event, concepts, extra = {}) => {
  if (!concepts.length) return;
  Promise.resolve()
    .then(() => blinkscribeapi.post("/v1/recording/order-suggestions/feedback", {
      appointmentid: Number(apptKey), patientid: Number(patientId), event, concepts, prechecked: [], ...extra,
    }))
    .catch(() => {});
};

export const SURFACES = ["chart", "orders", "dashboard"];
const COMPACT_ROWS = 4;

/* Why the Order button is disabled (null = enabled): the same rule as before (practice shows CareIQ orders AND the
   visit is today's and held), split by reason for the telemetry and the message. */
export const orderDisabledReason = (chart, now = new Date()) => {
  if (!careiqOrdersShown()) return "orders-off";
  const v = classifyVisit(chart, now);
  if (v.kind === "none") return "no-visit";
  if (v.kind === "unknown") return "unknown-date";
  if (v.kind !== "today") return "not-today";
  if (visitNotHeld(chart)) return "not-held";
  return null;
};
// The compact title (review 10-26): "heard in this visit" only when every open row came from the conversation;
// all routine (history) rows -> "routine"; a mix (or scheduled rows) -> "suggested".
export const compactTitle = (rows) => {
  const open = (rows || []).filter((r) => r.status !== "ordered");
  if (open.length && open.every((r) => r.source === "voice" || r.source === "heard")) return "heard in this visit";
  if (open.length && open.every((r) => r.source === "routine")) return "routine";
  return "suggested";
};
const DISABLED_TEXT = {
  "orders-off": "Orders are not placed from here for this practice.",
  "not-held": "This visit is cancelled or a no-show: orders can be placed on a held visit only.",
};

// "Maria Delgado" / "10:30 AM" of the chart's visit row (shown by the end-of-visit review and its toast).
export const visitLabelOf = (chart) => {
  if (!chart) return null;
  const name = [chart.firstname, chart.lastname].filter((x) => x && String(x).trim()).join(" ").trim();
  const time = visitTimeLabel(chart);
  return name || time ? { name, time } : null;
};

// The visit-row fields the card uses: a dashboard refresh that rebuilds the row objects with the same values keeps the
// same chart (no re-registration, no re-fetch).
const CHART_FIELDS = ["appointmentid", "patientid", "startdate", "starttime", "firstname", "lastname", "encounterid",
  "linked_provider", "providername", "appointmentstatus", "visit_confirmation", "status", "noshow", "no_show"];
const chartSig = (c) => (c ? CHART_FIELDS.map((f) => String(c[f] ?? "")).join("\u0001") : "");

let pollSeq = 0;

const LiveOrdersCard = ({ patientId, chart: chartProp, surface: surface0 = "chart" }) => {
  const sig = chartSig(chartProp);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const chart = useMemo(() => chartProp, [sig]);
  const pollToken = useRef(null);
  if (pollToken.current === null) { pollSeq += 1; pollToken.current = pollSeq; }
  const surface = SURFACES.includes(surface0) ? surface0 : "chart";
  const compact = surface !== "chart";
  const { liveOrders: socketLive, isGlobalRecording } = useRecording();
  const userConfig = useSelector((s) => s.userconfig);
  const me = (userConfig && (userConfig.username || (userConfig.config && userConfig.config.username))) || "";
  const pid = patientId != null ? String(patientId) : "";
  const appt = chart ? normalizeApptId(chart.appointmentid) : null;
  const apptKey = appt ? String(appt) : "";
  const visitKey = visitKeyOf(pid, apptKey);
  const storeKey = reviewStoreKey(pid, apptKey);
  const [storedTick, setStoredTick] = useState(0);
  const [rec, setRec] = useState(() => (storeKey ? readRecord(storeKey) : null));
  const [visitOrders, setVisitOrders] = useState(null);
  const [, setFlightTick] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [whenBusy, setWhenBusy] = useState(() => new Map());   // concept -> "busy" | error line (a placed row's switch)
  const firstSeen = useRef(new Map());     // concept -> when this card first rendered it (highlight)
  const initial = useRef(null);            // the rows present when the card first had rows (never highlighted)

  const lo = useMemo(() => {
    const a = liveStateFor(socketLive, pid, apptKey || null);
    const b = liveStateFor(storedLive(pid, apptKey), pid, apptKey || null);
    // while this visit is recorded the socket is the truth (a stored block may be an earlier recording's final rows)
    return apptKey ? newerLive(a, b, { preferSocket: Boolean(isGlobalRecording) }) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketLive, pid, apptKey, storedTick, isGlobalRecording]);
  const disabledReason = lo ? orderDisabledReason(chart) : null;
  const canOrder = Boolean(lo) && !disabledReason;
  // ids / words only: on every live_* feedback line (hubfix-oi-1026)
  const tele = useMemo(() => ({ can_order: canOrder, disabled_reason: disabledReason || undefined, surface }), [canOrder, disabledReason, surface]);
  const [showAll, setShowAll] = useState(false);
  const [, setHiddenTick] = useState(0);
  const info = useMemo(() => (lo && chart ? {
    practiceId: getPracticeId(), patientId: pid, apptKey, ctx: { ...visitOrderContext(chart, me), fallbackAppointmentId: chart.appointmentid }, canOrder,
    label: visitLabelOf(chart),
  } : null), [lo, chart, me, pid, apptKey, canOrder]);

  useEffect(() => { setRec(storeKey ? readRecord(storeKey) : null); }, [storeKey]);
  useEffect(() => {
    if (info && visitKey) registerLiveVisit(visitKey, info);
    if (info && visitKey && info.label) setOrderReviewLabel(visitKey, info.label);
  }, [info, visitKey]);
  useEffect(() => {
    // only the chart's card means "this visit's chart is open" (the review opens by itself there only)
    if (!visitKey || surface !== "chart") return undefined;
    cardMounted(visitKey, 1);
    return () => { cardMounted(visitKey, -1); };
  }, [visitKey, surface]);
  // the final payload stored after the stop (LiveOrdersHost / CareIQ) and the review's choices
  useEffect(() => {
    const on = () => {
      setStoredTick((n) => n + 1);
      if (storeKey) setRec(readRecord(storeKey));
    };
    const onStorage = (e) => { if (!e || !e.key || e.key === storeKey) on(); };   // another browser tab
    const onFlight = () => setFlightTick((n) => n + 1);
    eventBus.on("liveOrdersStored", on);
    eventBus.on("orderReviewChanged", on);
    eventBus.on("liveOrdersInflight", onFlight);
    window.addEventListener("storage", onStorage);
    return () => {
      eventBus.off("liveOrdersStored", on);
      eventBus.off("orderReviewChanged", on);
      eventBus.off("liveOrdersInflight", onFlight);
      window.removeEventListener("storage", onStorage);
    };
  }, [storeKey]);

  // one read per visit at a time page-wide (sharedVisitOrders); every card of the visit gets the result
  const refresh = useCallback(async (opts) => {
    if (!info) return;
    const list = await sharedVisitOrders(visitKey, info, { fresh: Boolean(opts && opts.fresh) });
    if (Array.isArray(list)) setVisitOrders(list);
    setNow(Date.now());
  }, [info, visitKey]);
  useEffect(() => {
    const on = (e) => { if (e && e.visitKey === visitKey && Array.isArray(e.list)) setVisitOrders(e.list); };
    eventBus.on("liveVisitOrders", on);
    return () => eventBus.off("liveVisitOrders", on);
  }, [visitKey]);
  const rows = useMemo(() => cardRows(lo, { rec, visitOrders, now }), [lo, rec, visitOrders, now]);
  // when this card first rendered each row (a row arriving after the card is up is highlighted briefly)
  rows.forEach((r) => { if (!firstSeen.current.has(r.concept)) firstSeen.current.set(r.concept, Date.now()); });
  if (initial.current === null && rows.length) initial.current = new Set(rows.map((r) => r.concept));
  const nOpen = openCount(rows);
  const rowCount = lo ? lo.rows.length : 0;
  // a compact surface's ✕ (review 10-26: decided BEFORE the effects - a hidden card never reads or polls)
  const openConcepts = rows.filter((r) => r.status !== "ordered").map((r) => r.concept);
  const hiddenHere = compact && (!openConcepts.length || surfaceIsHidden(visitKey, surface, openConcepts));
  const active = Boolean(lo) && rows.length > 0 && !hiddenHere;
  // the visit's orders: on a new row, every 20 s while rows are open (ONE card per visit polls, claimPoll), and on
  // any order change elsewhere
  useEffect(() => { if (rowCount && active) refresh(); }, [rowCount, active, refresh]);
  useEffect(() => {
    if (!nOpen || !active || !visitKey) return undefined;
    const tok = pollToken.current;
    claimPoll(visitKey, tok);
    const t = setInterval(() => { if (claimPoll(visitKey, tok)) refresh(); }, LIVE_POLL_MS);
    return () => { clearInterval(t); releasePoll(visitKey, tok); };
  }, [nOpen, active, refresh, visitKey]);
  useEffect(() => {
    if (!active) return undefined;
    const on = () => refresh();          // after changed() invalidated the visit's read: a fresh one, shared
    eventBus.on("fetchSummary", on);
    return () => eventBus.off("fetchSummary", on);
  }, [refresh, active]);
  // Undo expires (2 min) and the highlight fades without a new push
  const hasUndo = rows.some((r) => r.canUndo);
  const hasNew = rows.some((r) => initial.current && !initial.current.has(r.concept) && now - (firstSeen.current.get(r.concept) || 0) < LIVE_NEW_MS);
  useEffect(() => {
    if (!hasUndo && !hasNew) return undefined;
    const t = setTimeout(() => setNow(Date.now()), hasNew ? 1000 : 5000);
    return () => clearTimeout(t);
  }, [hasUndo, hasNew, now]);

  const save = useCallback((next) => {
    if (!storeKey) return;
    writeRecord(storeKey, next);
    setRec(next);
    eventBus.emit("orderReviewChanged");             // the other mounted cards / the review re-read the record
  }, [storeKey]);

  // one live_shown line per row per visit
  useEffect(() => {
    if (!storeKey || !rows.length) return;
    const base = readRecord(storeKey) || emptyRecord();
    const { fresh, rec: next } = markShown(base, rows);
    if (!fresh.length) return;
    save(next);
    ["voice", "heard", "chart", "routine"].forEach((src) => {
      const cs = fresh.filter((r) => r.source === src).map((r) => r.concept);
      feedback(pid, apptKey, "live_shown", cs, { source: src, ...tele });
    });
  }, [rows, storeKey, save, pid, apptKey, tele]);
  // once per visit + surface: where the card was seen, and whether it could order there (not while hidden)
  useEffect(() => {
    if (!visitKey || !active || !markSurface(visitKey, surface)) return;
    feedback(pid, apptKey, "live_surface", rows.map((r) => r.concept), { rows_n: rows.length, ...tele });
  }, [rows, active, visitKey, surface, pid, apptKey, tele]);

  const onOrder = useCallback(async (row0) => {
    if (!info || !canOrder || !storeKey) return;
    // the Today / Schedule mode as the record has it now (a tap in another tab counts)
    const row = { ...row0, when: whenFor(readRecord(storeKey), row0) };
    // another card / tab may have placed it already: the record is re-read, never this render's copy
    let latest = readRecord(storeKey) || emptyRecord();
    const mine = latest.liveOrdered && latest.liveOrdered[row.concept];
    if (mine) {
      // re-review 10-23: only when that order is STILL on the visit (deleted elsewhere = clear the entry and place)
      const list = await visitOrdersOf(info);
      if (!Array.isArray(list) || orderStillOnVisit(mine, list)) {
        if (Array.isArray(list)) setVisitOrders(list);
        setRec(latest);
        return;
      }
      latest = recordUndone(readRecord(storeKey) || latest, row.concept);
      save(latest);
    }
    if (inflightIn(latest, row.concept)) return;                    // another browser tab is placing it
    if (!beginFlight(visitKey, row.concept)) return;                // a tap on this row is already placing it
    save(recordInflight(readRecord(storeKey) || latest, row.concept));
    let orderId = null;
    let ordertype = null;
    try {
      ({ orderId, ordertype } = await placeLiveRow(info, row));
    } catch (_) {
      orderId = null;
    }
    const age = rowAge(row);
    if (orderId == null) {
      save(clearInflight(readRecord(storeKey) || emptyRecord(), row.concept));
      endFlight(visitKey, row.concept, true);
      feedback(pid, apptKey, "live_failed", [row.concept], { source: row.source, age_ms: age, ...tele });
      return;
    }
    save(clearInflight(recordOrdered(readRecord(storeKey) || emptyRecord(), row, orderId, ordertype), row.concept));
    endFlight(visitKey, row.concept, false);
    setNow(Date.now());
    feedback(pid, apptKey, "live_ordered", [row.concept], { source: row.source, age_ms: age, when: row.when, when_default: row.whenDefault || undefined, ...tele });
    refresh();     // changed() dropped the read in flight: this joins the FRESH post-placement read
  }, [info, canOrder, save, storeKey, visitKey, pid, apptKey, refresh, tele]);

  const onUndo = useCallback(async (row) => {
    const cur = storeKey ? readRecord(storeKey) : null;
    const mine = cur && cur.liveOrdered ? cur.liveOrdered[row.concept] : null;
    if (!info || !mine) {
      if (cur) setRec(cur);
      return;
    }
    if (!beginFlight(visitKey, row.concept)) return;
    let ok = false;
    try {
      ok = await undoLiveOrder(info, { orderId: mine.order_id, ordertype: mine.ordertype });
    } catch (_) {
      ok = false;
    }
    if (!ok) {
      endFlight(visitKey, row.concept, true);
      return;
    }
    save(recordUndone(readRecord(storeKey) || emptyRecord(), row.concept));
    endFlight(visitKey, row.concept, false);
    feedback(pid, apptKey, "live_undone", [row.concept], {
      source: row.source || mine.source || undefined, age_ms: Math.max(0, Date.now() - (Number(mine.at) || 0)), ...tele,
    });
    refresh();     // changed() dropped the read in flight: this joins the FRESH post-Undo read
  }, [info, save, storeKey, visitKey, pid, apptKey, refresh, tele]);

  // Today / Schedule (owner 10-24): the tap is kept in the visit's record (the review opens with the same choice)
  const onWhen = useCallback((row) => {
    const next = otherWhen(row.when);
    save(recordWhen(readRecord(storeKey) || emptyRecord(), row.concept, next));
    feedback(pid, apptKey, "live_when", [row.concept], { source: row.source, when: next, when_default: row.whenDefault || undefined, ...tele });
  }, [save, storeKey, pid, apptKey, tele]);

  // A placed row's switch (owner 10-24): the dashboard's existing Today <-> Schedule call on THIS visit's order,
  // read back; the row shows the result, or the error.
  const onWhenPlaced = useCallback(async (row) => {
    if (!info || row.orderId == null || whenBusy.get(row.concept) === "busy") return;
    if (!placedWhenEditable(info.ctx && info.ctx.orderDate)) return;   // review 10-24 H1: today's visit only
    const next = otherWhen(row.when);
    setWhenBusy((m) => new Map(m).set(row.concept, "busy"));
    let r = { ok: false, error: "Not changed · try again" };
    try {
      r = await changeLiveOrderWhen(info, row.orderId, next);
    } catch (_) { /* r stays the error */ }
    if (r.ok) {
      const cur = readRecord(storeKey) || emptyRecord();
      const mine = cur.liveOrdered && cur.liveOrdered[row.concept];
      const lo2 = { ...(cur.liveOrdered || {}) };
      if (mine) lo2[row.concept] = { ...mine, when: next };
      save({ ...recordWhen(cur, row.concept, next), liveOrdered: lo2 });
      feedback(pid, apptKey, "live_when", [row.concept], { source: row.source || undefined, when: next, when_default: row.whenDefault || undefined, placed: [row.concept], ...tele });
      setWhenBusy((m) => { const n = new Map(m); n.delete(row.concept); return n; });
      refresh();
    } else {
      setWhenBusy((m) => new Map(m).set(row.concept, r.error || "Not changed · try again"));
    }
  }, [info, whenBusy, storeKey, save, pid, apptKey, refresh, tele]);

  const onDismiss = useCallback((row) => {
    save(recordDismissed(readRecord(storeKey) || emptyRecord(), row.concept));
    feedback(pid, apptKey, "live_dismissed", [row.concept], { source: row.source, age_ms: rowAge(row), ...tele });
  }, [save, storeKey, pid, apptKey, tele]);

  // a compact surface's ✕: hidden there until a row arrives that was not open when it was hidden
  const onHide = () => {
    hideSurface(visitKey, surface, openConcepts);
    setHiddenTick((n) => n + 1);
    feedback(pid, apptKey, "live_hidden", openConcepts, { rows_n: rows.length, ...tele });
  };

  if (!lo || !rows.length) return null;
  if (hiddenHere) return null;
  // routine rows (history, not the conversation) are low-key and come after the rows heard in the visit
  const ordered = [...rows.filter((r) => r.source !== "routine"), ...rows.filter((r) => r.source === "routine")];
  const shown = compact && !showAll ? ordered.slice(0, COMPACT_ROWS) : ordered;

  return (
    <div
      style={compact ? { ...box, margin: "4px 0 6px", padding: "6px 8px" } : box}
      data-testid="live-orders-card"
      data-surface={surface}
      data-can-order={canOrder ? "1" : "0"}
    >
      <style>{"@keyframes lvoFlash { 0% { background: #FEF3C7; } 100% { background: transparent; } }"}</style>
      <div style={titleS}>
        <span>Orders</span>
        <span style={{ fontWeight: 500, color: "#6B7280" }}>{nOpen ? `${nOpen} suggested` : "all ordered"}</span>
        {compact ? <span style={{ fontWeight: 500, color: "#6B7280" }}>{`· ${compactTitle(rows)}`}</span> : null}
        <span style={{ marginLeft: "auto", fontWeight: 500, color: "#6B7280" }}>one tap orders it</span>
        {compact ? (
          <button type="button" style={xS} title="Hide" aria-label="Hide suggested orders" onClick={onHide}>✕</button>
        ) : null}
      </div>
      {!canOrder ? (
        <div style={{ ...whyS, whiteSpace: "normal", marginBottom: 4 }} role="status" data-reason={disabledReason || undefined}>
          {DISABLED_TEXT[disabledReason] || "Orders can be placed on today’s visit only."}
        </div>
      ) : null}
      {shown.map((r) => {
        const seenAt = firstSeen.current.get(r.concept) || 0;
        const fresh = Boolean(initial.current) && !initial.current.has(r.concept) && now - seenAt < LIVE_NEW_MS;
        const isBusy = isInflight(visitKey, r.concept) || inflightIn(rec, r.concept, Date.now());
        const routine = r.source === "routine";
        const pct = routine && Number.isFinite(Number(r.p)) ? Math.round(Number(r.p) * 100) : null;
        return (
          <div
            key={r.concept}
            style={{ ...rowS, animation: fresh ? "lvoFlash 3s ease-out" : undefined, ...(routine ? { color: "#4B5563" } : {}) }}
            data-concept={r.concept}
            data-source={r.source}
            data-status={r.status}
            data-fresh={fresh ? "1" : undefined}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: routine ? 500 : 600 }}>{r.order_name}</div>
              {r.reason ? (
                <div style={routine ? { ...whyS, fontStyle: "italic" } : whyS} title={r.reason}>
                  {r.reason}
                  {pct != null ? <span data-testid="routine-conf">{` · ${pct}% likely`}</span> : null}
                </div>
              ) : null}
              {isFailed(visitKey, r.concept) ? <div style={{ ...whyS, color: "#B91C1C" }} role="status">Not done · try again</div> : null}
              {whenBusy.get(r.concept) === "busy" ? <div style={whyS} role="status">Changing…</div> : null}
              {whenBusy.get(r.concept) && whenBusy.get(r.concept) !== "busy"
                ? <div style={{ ...whyS, color: "#B91C1C", whiteSpace: "normal" }} role="alert">{whenBusy.get(r.concept)}</div> : null}
            </div>
            {r.status === "ordered" ? (
              <>
                <span style={{ marginLeft: "auto" }}>
                  <WhenToggle
                    name={r.order_name}
                    when={r.when}
                    disabled={whenBusy.get(r.concept) === "busy" || isBusy}
                    readOnly={r.orderId == null || !placedWhenEditable(info && info.ctx && info.ctx.orderDate)}
                    onToggle={() => onWhenPlaced(r)}
                  />
                </span>
                <span style={{ color: "#047857", fontWeight: 700, fontSize: 11, whiteSpace: "nowrap" }}>Ordered ✓</span>
                {r.canUndo ? (
                  <button type="button" style={linkS} disabled={isBusy} onClick={() => onUndo(r)} aria-label={`Undo ${r.order_name}`}>
                    {isBusy ? "…" : "Undo"}
                  </button>
                ) : null}
              </>
            ) : (
              <>
                <span style={{ marginLeft: "auto" }}><WhenToggle name={r.order_name} when={r.when} disabled={isBusy} onToggle={() => onWhen(r)} /></span>
                <button
                  type="button"
                  style={{
                    ...btnS, marginLeft: 0, opacity: canOrder && !isBusy ? 1 : 0.5, cursor: canOrder && !isBusy ? "pointer" : "default",
                    ...(routine ? { background: "#fff", color: "#2563EB", fontWeight: 600 } : {}),
                  }}
                  disabled={!canOrder || isBusy}
                  onClick={() => onOrder(r)}
                  aria-label={`Order ${r.order_name}`}
                >
                  {isBusy ? "Ordering…" : "Order"}
                </button>
                <button type="button" style={xS} title="Dismiss" aria-label={`Dismiss ${r.order_name}`} onClick={() => onDismiss(r)}>
                  ✕
                </button>
              </>
            )}
          </div>
        );
      })}
      {compact && rows.length > shown.length ? (
        <button type="button" style={linkS} onClick={() => setShowAll(true)}>{`Show all ${rows.length}`}</button>
      ) : null}
    </div>
  );
};

export { LIVE_UNDO_MS };
export default LiveOrdersCard;
